import { SparklesIcon } from '@heroicons/react/24/outline';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useAIAssistantStore } from '@/apps/AIAssistant/stores/useAIAssistantStore';
import { AIRecommendation } from '@/apps/AIAssistant/types';
import Button from '@/common/components/Button';

export const AIAssistant: React.FC = () => {
  const { t } = useTranslation();
  const {
    recommendations,
    loading,
    error,
    lastUpdated,
    generateRecommendations,
    applyRecommendation,
    loadFromLocalStorage,
    dismissRecommendation,
  } = useAIAssistantStore();

  useEffect(() => {
    loadFromLocalStorage();
  }, [loadFromLocalStorage]);

  const handleGenerateRecommendations = async () => {
    await generateRecommendations();
  };

  const handleDismiss = (recommendation: AIRecommendation) => {
    dismissRecommendation(recommendation.id);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1">
          {lastUpdated && (
            <p className="text-sm text-gray-500">
              {t('aiAssistant.lastUpdated')}: {new Date(lastUpdated).toLocaleString()}
            </p>
          )}
        </div>
        <Button
          leftIcon={<SparklesIcon />}
          loading={loading}
          size="sm"
          variant="primary"
          onClick={handleGenerateRecommendations}
        >
          {t('aiAssistant.generateRecommendations')}
        </Button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
          {error}
        </div>
      )}

      <div className="grid gap-4">
        {recommendations.length > 0 &&
          recommendations.map((recommendation) => (
            <div
              key={recommendation.id}
              className="p-4 rounded-lg border border-gray-200 bg-white shadow-sm relative"
            >
              <button
                aria-label={t('aiAssistant.dismissRecommendation')}
                className="absolute top-2 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                onClick={() => handleDismiss(recommendation)}
              >
                ✕
              </button>
              <div className="flex justify-between items-start gap-4 mt-2">
                <div className="flex-grow">
                  <div className="flex items-center gap-2 mb-2">
                    <span
                      className={`px-2 py-1 rounded text-xs ${
                        recommendation.type === 'TASK'
                          ? 'bg-blue-100 text-blue-800'
                          : recommendation.type === 'ALERT'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-green-100 text-green-800'
                      }`}
                    >
                      {recommendation.type}
                    </span>
                    <span className="text-sm text-gray-500">{t('aiAssistant.from')} {recommendation.source}</span>
                    {recommendation.type === 'TASK' && (
                      <button
                        className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                        onClick={() => applyRecommendation(recommendation)}
                      >
                        {t('aiAssistant.addToChecklist')}
                      </button>
                    )}
                  </div>
                  <p className="text-gray-800">{recommendation.content}</p>
                </div>
              </div>
            </div>
          ))}

        {recommendations.length === 0 && !loading && (
          <div className="text-center text-gray-500 py-8">
            {t('aiAssistant.noRecommendations')}
          </div>
        )}

        {loading && (
          <div className="text-center text-gray-500 py-8">{t('aiAssistant.generating')}</div>
        )}
      </div>
    </div>
  );
};
